/**
 * API Utility
 * Provides helper functions for making authenticated API requests to the backend
 */

import { notifySessionInvalid } from './sessionInvalidation';

function endpointPathOnly(endpoint) {
  const s = String(endpoint || '');
  const q = s.indexOf('?');
  return q >= 0 ? s.slice(0, q) : s;
}

/** Endpoints where 401 must not trigger forced login (wrong OTP, logout, etc.). */
function shouldSkipSessionRedirectForEndpoint(endpoint) {
  const p = endpointPathOnly(endpoint);
  // /api/me returns 401 when there is no session — normal for public pages (e.g. /donate), not a forced logout.
  if (p === '/api/me') return true;
  if (p === '/api/verify-otp') return true;
  if (p === '/api/logout') return true;
  if (p === '/request-otp') return true;
  if (p.startsWith('/public/')) return true;
  return false;
}

function isPortalSessionAuthFailure(status, responseData) {
  if (status === 401) return true;
  if (status === 403) {
    const msg = String(responseData?.error || responseData?.message || '').toLowerCase();
    return (
      msg.includes('sign in again')
      || msg.includes('portal access required')
      || msg.includes('portal profile incomplete')
      || msg.includes('session expired')
      || msg.includes('session idle')
      || msg.includes('unauthorized')
    );
  }
  return false;
}

/**
 * Get authentication headers for API requests
 * @returns {Object} Headers object with authentication if available
 */
export const getAuthHeaders = () => {
  // Session: optional header token; HttpOnly cookie is sent via credentials: 'include'
  const headers = {
    // Add any required headers for your backend API
    'X-Requested-With': 'XMLHttpRequest'
  };
  try {
    const token = sessionStorage.getItem('session_token');
    if (token) headers['x-session-token'] = token;
  } catch {}
  return headers;
};

/**
 * Make an authenticated API request
 * @param {string} endpoint - The API endpoint (without base URL)
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Object>} The parsed JSON response
 */
// No retry configuration - all calls will be made exactly once

export const apiRequest = async (endpoint, options = {}) => {
  // Use the base URL from environment variables (fallback to localhost:4001 in dev)
  let baseUrl = process.env.REACT_APP_API_BASE_URL || '';
  if (!baseUrl) {
    try {
      const loc = typeof window !== 'undefined' ? window.location : null;
      const isDev = !!(loc && /localhost|127\.0\.0\.1/.test(loc.hostname));
      if (isDev) baseUrl = 'http://localhost:4001';
    } catch {}
  }
  // Ensure base URL doesn't end with a slash
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  // Ensure endpoint starts with a slash
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${cleanBaseUrl}${normalizedEndpoint}`;

  const reqId = Math.random().toString(36).slice(2, 8);
  const method = options.method || 'GET';
  const label = `API ${method} ${endpoint} [${reqId}]`;
  console.log('API Request:', { id: reqId, method, url, endpoint });
  let timerStarted = false;
  try {
    if (typeof console.time === 'function') {
      console.time(label);
      timerStarted = true;
    }
  } catch {}

  // Default timeout: 30 seconds (30000ms)
  const timeoutMs = options.timeout !== undefined ? options.timeout : 30000;
  let abortController = null;
  let timeoutId = null;

  try {
    // Set up timeout using AbortSignal.timeout if available (modern browsers)
    // Otherwise use AbortController with setTimeout
    let signal = options.signal;
    if (!signal && timeoutMs > 0) {
      if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) {
        // Modern browsers support AbortSignal.timeout
        try {
          signal = AbortSignal.timeout(timeoutMs);
        } catch (e) {
          // Fallback if timeout() is not supported
          abortController = new AbortController();
          signal = abortController.signal;
          timeoutId = setTimeout(() => {
            abortController.abort();
          }, timeoutMs);
        }
      } else {
        // Fallback for older browsers
        abortController = new AbortController();
        signal = abortController.signal;
        timeoutId = setTimeout(() => {
          abortController.abort();
        }, timeoutMs);
      }
    }

    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      credentials: 'include', // Include cookies if using session-based auth
      ...options,
      signal // Add signal to options
    };

    const response = await fetch(url, defaultOptions);
    const status = response.status;
    const ok = response.ok;
    let responseData = {};
    try { responseData = await response.json(); } catch { responseData = {}; }

    try {
      console.log('API Response:', { id: reqId, method, url, endpoint, status, ok });
    } catch {}

    // Persist session token if backend returned one (header-based session auth)
    try {
      if (responseData && typeof responseData === 'object' && responseData.session_token) {
        sessionStorage.setItem('session_token', responseData.session_token);
      }
    } catch {}

    // Handle error statuses
    if (!ok) {
      // Prefer server-provided error/message fields
      const serverMessage = (
        (typeof responseData === 'string' && responseData) ||
        responseData?.message ||
        responseData?.error ||
        responseData?.error?.message ||
        (Array.isArray(responseData?.errors) && responseData.errors[0]?.message) ||
        response.statusText ||
        'API request failed'
      );
      const error = new Error(serverMessage);
      error.status = response.status;
      error.data = responseData;
      error.url = url;
      error.method = defaultOptions.method || 'GET';
      if (
        isPortalSessionAuthFailure(response.status, responseData)
        && !shouldSkipSessionRedirectForEndpoint(endpoint)
      ) {
        try {
          notifySessionInvalid({ message: serverMessage, status: response.status });
        } catch {
          /* ignore */
        }
      } else if (response.status === 401) {
        try {
          console.warn('API 401:', { endpoint, url, status: response.status, message: serverMessage });
        } catch {
          /* ignore */
        }
      }
      try {
        if (timerStarted && typeof console.timeEnd === 'function') {
          console.timeEnd(label);
        }
      } catch {}
      throw error;
    }

    try {
      if (timerStarted && typeof console.timeEnd === 'function') {
        console.timeEnd(label);
      }
    } catch {}
    // Clear timeout if it was set manually
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    return responseData;
  } catch (error) {
    // Clear timeout if it was set manually
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    
    // Check if error is due to timeout/abort
    if (error.name === 'AbortError' || error.name === 'TimeoutError') {
      const timeoutError = new Error(`Request timeout after ${timeoutMs}ms: ${endpoint}`);
      timeoutError.name = 'TimeoutError';
      timeoutError.status = 408;
      timeoutError.url = url;
      timeoutError.method = method;
      console.error('API request timeout:', {
        id: reqId,
        message: timeoutError.message,
        method,
        url,
        endpoint,
        timeoutMs
      });
      try {
        if (timerStarted && typeof console.timeEnd === 'function') {
          console.timeEnd(label);
        }
      } catch {}
      throw timeoutError;
    }
    
    // Ensure we log enough context to diagnose
    console.error('API request failed:', {
      id: reqId,
      message: error?.message,
      status: error?.status,
      method: error?.method || method,
      url: error?.url || url,
      data: error?.data
    });
    try {
      if (timerStarted && typeof console.timeEnd === 'function') {
        console.timeEnd(label);
      }
    } catch {}
    throw error;
  }
};

/**
 * Make a GET request
 * @param {string} endpoint - The API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} The response data
 */
export const get = (endpoint, params = {}, options = {}) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && String(v) !== 'undefined')
  );
  const queryString = new URLSearchParams(clean).toString();
  const url = queryString ? `${endpoint}?${queryString}` : endpoint;
  return apiRequest(url, { 
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    ...options
  });
};

/**
 * Make a POST request
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - The request body
 * @returns {Promise<Object>} The response data
 */
// Helper function to safely stringify objects with circular references
const safeStringify = (obj) => {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  });
};

export const post = (endpoint, data = {}, options = {}) => {
  // Clean the data object to remove any circular references
  const cleanData = JSON.parse(safeStringify(data));
  
  return apiRequest(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify(cleanData),
    ...options
  });
};

/**
 * Make a PUT request
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - The request body
 * @returns {Promise<Object>} The response data
 */
export const put = (endpoint, data = {}, options = {}) => {
  return apiRequest(endpoint, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify(data),
    ...options
  });
};

/**
 * Make a PATCH request
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - The request body
 * @returns {Promise<Object>} The response data
 */
export const patch = (endpoint, data = {}, options = {}) => {
  return apiRequest(endpoint, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify(data),
    ...options
  });
};

/**
 * Make a DELETE request
 * @param {string} endpoint - The API endpoint
 * @returns {Promise<Object>} The response data
 */
export const del = (endpoint) => {
  return apiRequest(endpoint, { 
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  });
};

// Export all functions
export default {
  get,
  post,
  put,
  patch,
  delete: del,
  request: apiRequest,
  getAuthHeaders
};
